import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Action, Subject } from '@erp/authz';
import { rpcClient } from 'src/api/rpc/client';
import { useCan } from 'src/hooks/useCan';
import { invalidateTaskQueries } from './invalidateTaskQueries';

/**
 * The task's open help request (`tasks.getOpenHelpRequest`) — the structured
 * question an agent recorded when it escalated the task to `needs_input`.
 * Null while the task is in any other status.
 */
export function useOpenTaskHelpRequest(taskId: string | null | undefined) {
  const [canReadTasks] = useCan(Action.Read, Subject.Task);
  return useQuery({
    queryKey: ['task-help-request', taskId],
    queryFn: () => rpcClient.tasks.getOpenHelpRequest({ id: taskId ?? '' }),
    enabled: canReadTasks && !!taskId,
  });
}

/**
 * The caller's Nami conversation on this task (`tasks.getAgentThread`): the
 * thread to resume on the task page, plus Nami's agent-definition id so the
 * UI can recognize her open help requests.
 */
export function useTaskAgentThread(taskId: string | null | undefined) {
  const [canReadTasks] = useCan(Action.Read, Subject.Task);
  return useQuery({
    queryKey: ['task-agent-thread', taskId],
    queryFn: () => rpcClient.tasks.getAgentThread({ id: taskId ?? '' }),
    enabled: canReadTasks && !!taskId,
  });
}

/**
 * Answer the agent's open help request (`tasks.provideInput`): records the
 * response and hands the task on — to Nami, who investigates in the
 * background and follows up through the task chat (`followUp: 'nami'`), or
 * back to the escalating agent's queue (`followUp: 'agent'`). On success every
 * task surface plus the timeline, the help-request callout, and the agent
 * thread are refreshed (the task leaves `needs_input`, so the callout
 * disappears).
 */
export function useProvideTaskInput(taskId: string | null | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { response: string; helpRequestActionId?: string }) =>
      rpcClient.tasks.provideInput({ id: taskId ?? '', ...input }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['task-help-request', taskId],
      });
      void queryClient.invalidateQueries({
        queryKey: ['task-agent-thread', taskId],
      });
      void queryClient.invalidateQueries({
        queryKey: ['task-timeline', taskId],
      });
      invalidateTaskQueries(queryClient);
    },
  });
}
